import { IdCard } from 'lucide-react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { ServiceConnectionType } from '@/api/model'
import type { SageHrServiceConnectionModel } from '@/api/model'
import { SageHrDatabaseKeyPicker } from './SageHrDatabaseKeyPicker'
import {
  emptyServiceConnectionFormValues,
  PASSWORD_PLACEHOLDER,
  type ServiceConnectionFieldsProps,
  type ServiceConnectionFormValues,
  type ServiceConnectionTypeDefinition,
} from './types'

function SageHrFields({ form, isCreate }: ServiceConnectionFieldsProps) {
  const { t } = useTranslation('service-connections')
  const { register, watch, control } = form

  // The picker is not an <input>, so the field is driven by useController: it subscribes this
  // component to the value, registers it for submission, and marks the form dirty on change.
  // Neither `watch` + `setValue` nor a registered hidden input do all three here.
  const databaseKey = useController({ control, name: 'databaseKey' }).field

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="service-connection-api-base-url">
          {t('dialog.sageHr.apiBaseUrl.label')} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="service-connection-api-base-url"
          placeholder={t('dialog.sageHr.apiBaseUrl.placeholder')}
          {...register('apiBaseUrl', { required: true })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="service-connection-username">
          {t('dialog.sageHr.username.label')} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="service-connection-username"
          autoComplete="off"
          {...register('username', { required: true })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="service-connection-database-key">
          {t('dialog.sageHr.databaseKey.label')} <span className="text-destructive">*</span>
        </Label>
        <SageHrDatabaseKeyPicker
          apiBaseUrl={watch('apiBaseUrl')}
          value={databaseKey.value}
          onChange={databaseKey.onChange}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="service-connection-mandate-id">
          {t('dialog.sageHr.mandateId.label')} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="service-connection-mandate-id"
          type="number"
          inputMode="numeric"
          placeholder={t('dialog.sageHr.mandateId.placeholder')}
          {...register('mandateId', { required: true })}
        />
        <p className="text-sm text-muted-foreground">{t('dialog.sageHr.mandateId.hint')}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="service-connection-password">
          {t('dialog.sageHr.password.label')}
          {isCreate && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id="service-connection-password"
          type="password"
          autoComplete="new-password"
          {...register('password', { required: isCreate })}
        />
        {!isCreate && (
          <p className="text-sm text-muted-foreground">{t('dialog.sageHr.password.hintEdit')}</p>
        )}
      </div>
    </>
  )
}

// Sage installations without several mandates use 1, so a new connection starts there.
const defaultValues: ServiceConnectionFormValues = {
  ...emptyServiceConnectionFormValues,
  mandateId: '1',
}

/** An untouched password field must never overwrite the stored secret. */
function resolvePassword(password: string): string | null {
  const trimmed = password.trim()
  if (trimmed === '' || password === PASSWORD_PLACEHOLDER) {
    return null
  }
  return password
}

export const sageHrServiceConnection: ServiceConnectionTypeDefinition = {
  type: ServiceConnectionType.SageHr,
  icon: { kind: 'lucide', icon: IdCard },
  labelKey: 'types.SageHr.label',
  defaultValues,
  Fields: SageHrFields,

  // The caller already selected this definition by `type`, so the cast needs no re-check.
  fromModel: (model) => {
    const sageHr = model as SageHrServiceConnectionModel

    return {
      name: sageHr.name ?? '',
      apiBaseUrl: sageHr.apiBaseUrl ?? '',
      username: sageHr.username ?? '',
      databaseKey: sageHr.databaseKey ?? '',
      mandateId: String(sageHr.mandateId ?? ''),
      password: sageHr.isPasswordSet ? PASSWORD_PLACEHOLDER : '',
    }
  },

  toCreatePayload: (values) => ({
    type: ServiceConnectionType.SageHr,
    name: values.name.trim(),
    apiBaseUrl: values.apiBaseUrl.trim(),
    username: values.username.trim(),
    databaseKey: values.databaseKey.trim(),
    mandateId: Number(values.mandateId),
    password: values.password,
  }),

  toUpdatePayload: (values) => ({
    type: ServiceConnectionType.SageHr,
    name: values.name.trim(),
    apiBaseUrl: values.apiBaseUrl.trim(),
    username: values.username.trim(),
    databaseKey: values.databaseKey.trim(),
    mandateId: Number(values.mandateId),
    password: resolvePassword(values.password),
  }),
}
